import { useEffect, useRef, useState } from 'react';
import { Button } from '@cmsgov/ds-healthcare-gov';
import ExpenseFields from '../ExpenseFields';
import ExpenseRow from '../ExpenseRow';
import { formatCurrency } from '../../../utilities/currency';
import { sendEvent } from '../../../utilities/analytics';
import { useTranslation } from 'react-i18next';

import type { Expense } from '../IncomeCalculator';
import type { OrderedMap } from 'immutable';

type Props = {
  expenses: OrderedMap<string, Expense>;
  expensesTotal: number;
  onAddNewExpense: (expenseObject: Expense) => void;
  handleUpdateExpenseEntry: (expenseObject: Expense, id: string) => void;
  handleRemoveExpenseEntry: (id: string) => void;
};

const ExpenseContainer = (props: Props) => {
  const [currentlyAdding, setCurrentlyAdding] = useState<boolean>(false);
  const { t } = useTranslation();

  const handleAddNewExpense = (expenseRecord: Expense) => {
    sendEvent('estimate income', 'calculated income', 'expense', 'calculate_income_unauthenticated');
    props.onAddNewExpense(expenseRecord);
    setCurrentlyAdding(false);
  };

  const cachedElement = useRef<HTMLElement | null>(null);
  const firstUpdate = useRef(true);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    cachedElement.current?.focus();
  }, [props.expenses, props.expensesTotal, currentlyAdding]);

  return (
    <div className="ds-l-row ds-u-padding-y--3 ds-u-border-top--2 ds-u-border--dark">
      <div className="ds-l-col--12">
        <h2 className="ds-text-heading--2xl ds-u-justify-content--between ds-u-display--flex">
          <span>{t('expenses')}</span>
          <span>{formatCurrency(props.expensesTotal)}</span>
        </h2>
        <p>{t('expensesHint')}</p>
      </div>

      {props.expenses.entrySeq().map(([key, expense]) => (
        <ExpenseRow
          key={key}
          id={key}
          editing={false}
          expenseType={expense.expenseType}
          amount={expense.amount}
          expenseFrequency={expense.expenseFrequency}
          calculatedTotal={expense.calculatedTotal}
          onUpdateExpenseEntry={props.handleUpdateExpenseEntry}
          onRemoveExpense={props.handleRemoveExpenseEntry}
        />
      ))}

      {currentlyAdding ? (
        <ExpenseFields
          onCancelAddExpense={() => {
            setCurrentlyAdding(false);
          }}
          onDoneClick={handleAddNewExpense}
        />
      ) : (
        <div className="ds-l-col--12">
          <Button
            className="ic-add-expense ds-u-margin-top--3"
            inputRef={(ele) => {
              cachedElement.current = ele;
            }}
            onClick={() => setCurrentlyAdding(true)}
          >
            {t('addExpenseBtn')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ExpenseContainer;
